import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { PaginationData, PaginationRange } from 'tiime-material';

import { ApiCommonService } from '@api-services/api-common.service';
import { API_HEADERS, API_ROUTE_BASE, DOCUMENT_REQUEST_API_ROUTE_BASE } from '@constants/api-routes.constant';
import { ApiAlertError } from '@decorators/api-alert-error';
import {
  DocumentRequestFile,
  DocumentRequestTemplate,
  DocumentRequestTemplateJson
} from '@forms/document-request-template';
import { HttpHelper } from '@helpers/http.helper';
import { DocumentRequestFilters, DocumentRequestFiltersJson } from '@interfaces/document-request-filters';
import { DocumentRequest, DocumentRequestJson } from '@models/document-request';
import { FileSaverData } from '@services/file.service';

@Injectable({
  providedIn: 'root'
})
export class DocumentRequestsApiService extends ApiCommonService<DocumentRequest> {
  override readonly resourceUrl: string = DOCUMENT_REQUEST_API_ROUTE_BASE;
  private readonly templatesUrl = `${API_ROUTE_BASE}/document_requests/templates`;

  override fromJson(json: DocumentRequestJson): DocumentRequest {
    return DocumentRequest.fromJson(json);
  }

  override toJson(model: Partial<DocumentRequest>): Partial<DocumentRequestJson> {
    return DocumentRequest.toJson(model);
  }

  documentRequestFiltersJson(filters: DocumentRequestFilters): DocumentRequestFiltersJson {
    return {
      accounting_period: filters.accountingPeriod?.id || undefined,
      status: filters.documentRequestStatus === 'all' ? undefined : filters.documentRequestStatus ?? undefined
    };
  }

  @ApiAlertError()
  getDocumentRequests(
    range: PaginationRange,
    searchTerms: string,
    filters: DocumentRequestFilters,
    companyId: number
  ): Observable<PaginationData<DocumentRequest>> {
    let url = this.resourceUrl;
    let params = HttpHelper.createHttpParams();
    if (searchTerms?.length) {
      params = HttpHelper.setQParam(searchTerms, params);
    }

    if (companyId) {
      url = HttpHelper.replaceCompanyUrl(url, companyId);
    }

    if (filters) {
      params = HttpHelper.setParams(this.documentRequestFiltersJson(filters), params);
    }

    return this.getPaginated(range, params, url);
  }

  @ApiAlertError()
  getDocumentRequestTemplates(): Observable<DocumentRequestTemplate[]> {
    return this.http
      .get<DocumentRequestTemplateJson[]>(this.templatesUrl, {
        headers: new HttpHeaders({
          accept: API_HEADERS.documentRequestTemplateV2
        })
      })
      .pipe(map(json => json.map(template => DocumentRequestTemplate.fromJson(template))));
  }

  @ApiAlertError()
  createDocumentRequest(
    documentRequest: Pick<DocumentRequest, 'moduleType' | 'title' | 'description' | 'accountingPeriodId'>
  ): Observable<DocumentRequest> {
    return this.http
      .post<DocumentRequestJson>(this.resourceUrl, DocumentRequest.toJson(documentRequest))
      .pipe(map(json => this.fromJson(json)));
  }

  @ApiAlertError()
  closeDocumentRequest(documentRequest: DocumentRequest, option: { close: boolean }): Observable<DocumentRequest> {
    let url = `${this.resourceUrl}/${documentRequest.id}/close`;

    return this.http.patch<DocumentRequestJson>(url, option).pipe(map(json => this.fromJson(json)));
  }

  @ApiAlertError()
  downloadDocumentRequestTemplateFile(templateId: number): Observable<FileSaverData> {
    let url = `${this.templatesUrl}/${templateId}/file`;

    const blobObservable = this.http.get(url, {
      responseType: 'blob',
      observe: 'response'
    });

    return HttpHelper.getFileSaverData(blobObservable);
  }

  @ApiAlertError()
  uploadDocumentRequestFile(file: File): Observable<DocumentRequestFile> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http.post<DocumentRequestFile>(`${this.resourceUrl}/file_template`, formData);
  }
}
